import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import moment from "moment";
import AutoCompleteSearchBox from "../AutoCompleteSearchBox";
import searchImg from "../../assets/Images/search.png";

const presets = [
  { key: "all", label: "All" },
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "this_month", label: "This Month" },
  { key: "this_year", label: "This Year" },
  { key: "custom", label: "Custom" },
];


const getRangeForPreset = (key) => {
  switch (key) {
    case "all":
      return { startDate: null, endDate: null };
    case "today":
      return { startDate: moment().startOf("day").toDate(), endDate: moment().endOf("day").toDate() };
    case "week":
      return { startDate: moment().startOf("week").toDate(), endDate: moment().endOf("week").toDate() };
    case "this_month":
      return { startDate: moment().startOf("month").toDate(), endDate: moment().endOf("month").toDate() };
    case "this_year":
      return { startDate: moment().startOf("year").toDate(), endDate: moment().endOf("year").toDate() };
    default:
      return { startDate: null, endDate: null };
  }
};

const FilterSidebar = ({ open, onClose, initialFilters, onApply, onReset, moduleType = "subscription", setPage, tabType = "report" }) => {
  const [search, setSearch] = useState(initialFilters?.search || "");
  const [preset, setPreset] = useState(initialFilters?.preset || "all");
  const [planType, setPlanType] = useState(initialFilters?.planType || "all");
  const [userOption, setUserOption] = useState(initialFilters?.userOption || null);
  const panelRef = useRef(null);

  const initialRange = useMemo(() => getRangeForPreset(initialFilters?.preset || "all"), [initialFilters]);
  const [startDate, setStartDate] = useState(initialFilters?.startDate || null);
  const [endDate, setEndDate] = useState(initialFilters?.endDate || null);

  // Sync local state when sidebar opens or parent resets filters (e.g., on tab change)
  useEffect(() => {
    if (!open) return;
    setSearch(initialFilters?.search || "");
    setPreset(initialFilters?.preset || "all");
    setPlanType(initialFilters?.planType || "all");
    setUserOption(initialFilters?.userOption || null);
    setStartDate(initialFilters?.startDate || null);
    setEndDate(initialFilters?.endDate || null);
  }, [open, initialFilters]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  const handlePresetChange = (next) => {
    setPreset(next);
    if (next !== "custom") {
      const range = getRangeForPreset(next);
      setStartDate(range.startDate);
      setEndDate(range.endDate);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };


  const buildFilters = () => {
    const isStaticPreset = ["all", "today", "week", "this_month", "this_year"].includes(preset);
    const isSubscriptionTab = tabType === "report" || tabType === "user" || tabType === "cancelled";
    
    return {
      search: isSubscriptionTab 
        ? (userOption?.value ? "" : (userOption?.label || search)) 
        : search,
      preset,
      type: isStaticPreset ? preset : "custom",
      startDate: isStaticPreset ? "" : startDate,
      endDate: isStaticPreset ? "" : endDate,
      planType,
      userId: isSubscriptionTab ? (userOption?.value || null) : null,
      userOption: isSubscriptionTab ? (userOption || null) : null,
    };
  };

  const handleApply = () => {
    setPage?.(0);
    onApply?.(buildFilters());
    onClose?.();
  };

  const handleReset = () => {
    setSearch("");
    setPreset("all");
    setStartDate(null);
    setEndDate(null);
    setPlanType("all");
    setUserOption(null);
    onClose?.();
    onReset?.();
  };

  return (
    <div className={`subscription-filter-sidebar ${open ? "show" : ""}`}>
      <div ref={panelRef} className={`subscription-filter-panel ${open ? "show" : ""}`}>
        <div className="subscription-filter-header">
          <h3 className="subscription-filter-title">Filters</h3>
          <button
            type="button"
            className="subscription-filter-close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="subscription-filter-body">
          {moduleType !== "reports" && (
            <div className="subscription-filter-section">
              {(tabType === "report" || tabType === "user" || tabType === "cancelled") ? (
                <div className="subscription-filter-group">
                  <label className="subscription-filter-label">User</label>
                  <AutoCompleteSearchBox
                    onUserSelect={(selectedOption) => setUserOption(selectedOption)}
                    selectedUser={userOption}
                    placeholder="Search..."
                    loadOnMount={false}
                  />
                </div>
              ) : (
                <div className="subscription-filter-group">
                  <label className="subscription-filter-label">User Name</label>
                  <div className="subscription-search-container">
                    <div className="searchbar-container ms-auto">
                      <div className="position-relative">
                        <input
                          className="searchbar-custom"
                          type="text"
                          name="search"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search...."
                        />
                        {search ? (
                          <svg
                            stroke="currentColor"
                            fill="currentColor"
                            strokeWidth="0"
                            viewBox="0 0 512 512"
                            height="30px"
                            width="30px"
                            xmlns="http://www.w3.org/2000/svg"
                            className='searchbar-icon'
                            onClick={() => setSearch("")}
                          >
                            <path d="M256 48C141.1 48 48 141.1 48 256s93.1 208 208 208 208-93.1 208-208S370.9 48 256 48zm52.7 283.3L256 278.6l-52.7 52.7c-6.2 6.2-16.4 6.2-22.6 0-3.1-3.1-4.7-7.2-4.7-11.3 0-4.1 1.6-8.2 4.7-11.3l52.7-52.7-52.7-52.7c-3.1-3.1-4.7-7.2-4.7-11.3 0-4.1 1.6-8.2 4.7-11.3 6.2-6.2 16.4-6.2 22.6 0l52.7 52.7 52.7-52.7c6.2-6.2 16.4-6.2 22.6 0 6.2 6.2 6.2 16.4 0 22.6L278.6 256l52.7 52.7c6.2 6.2 6.2 16.4 0 22.6-6.2 6.3-16.4 6.3-22.6 0z"></path>
                          </svg>
                        ) : (
                          <img src={searchImg} alt="search icon" className="searchbar-icon" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {moduleType !== "reports" && (
            <div className="subscription-filter-section">
              <div className="subscription-filter-group">
                <label className="subscription-filter-label">Select Plan</label>
                <Select
                  options={[
                    { value: "all", label: "All" },
                    { value: "monthly", label: "Monthly" },
                    { value: "yearly", label: "Yearly" },
                  ]}
                  placeholder="Select Plan"
                  value={{ value: planType, label: planType.charAt(0).toUpperCase() + planType.slice(1) }}
                  onChange={(opt) => setPlanType(opt?.value || "all")}
                  isClearable={false}
                  theme={(base) => ({
                    ...base,
                    colors: {
                      ...base.colors,
                      primary: '#AD5ADC',
                      primary25: '#E8DBF3',
                      primary50: '#D2B9EA',
                    },
                  })}
                  styles={{
                    option: (styles, { isDisabled }) => ({
                      ...styles,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      marginTop: "0px",
                      marginBottom: "0px",
                      backgroundColor: styles.backgroundColor,
                    }),
                    control: (styles, state) => ({
                      ...styles,
                      border: state.isFocused ? "2px solid #AD5ADC" : "2px solid #00000061",
                      boxShadow: state.isFocused ? "0 0 0 1px #AD5ADC" : "none",
                      borderRadius: "20px",
                      minHeight: 40,
                      paddingLeft: 6,
                      ":hover": { borderColor: state.isFocused ? "#AD5ADC" : "#cfcfcf" },
                    }),

                    placeholder: (styles) => ({
                      ...styles,
                      color: "#6b6b6b",
                    }),
                    singleValue: (styles) => ({
                      ...styles,
                      color: "#000",
                      fontWeight: 400,
                    }),
                    dropdownIndicator: (styles, state) => ({
                      ...styles,
                      color: state.isFocused ? '#AD5ADC' : '#6b6b6b',
                      ':hover': { color: '#AD5ADC' },
                    }),
                    menu: (styles) => ({
                      ...styles,
                      borderRadius: "20px",
                      overflow: "hidden",
                      cursor: "pointer",
                    }),
                    option: (styles, { isFocused, isSelected }) => ({
                      ...styles,
                      backgroundColor: isSelected
                        ? '#E8DBF3'
                        : isFocused
                          ? '#F5ECFA'
                          : '#fff',
                      color: '#000',
                    }),
                  }}
                />
              </div>
            </div>
          )}

          {tabType !== "cancelled" && (
            <div className="subscription-filter-section">
              <div className="subscription-filter-group">
                <label className="subscription-filter-label">Quick Select</label>
                <Select
                  options={presets
                    .map(p => ({ value: p.key, label: p.label }))
                  }
                  placeholder="Select range"
                  value={{ value: preset, label: presets.find(p => p.key === preset)?.label || 'Custom' }}
                  onChange={(opt) => handlePresetChange(opt?.value || 'today')}
                  menuPosition="fixed"
                  isClearable={false}
                  theme={(base) => ({
                    ...base,
                    colors: {
                      ...base.colors,
                      primary: '#AD5ADC',
                      primary25: '#E8DBF3',
                      primary50: '#D2B9EA',
                    },
                  })}
                  styles={{
                    option: (styles, { isDisabled }) => ({
                      ...styles,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      marginTop: "0px",
                      marginBottom: "0px",
                      backgroundColor: styles.backgroundColor,
                    }),
                    control: (styles, state) => ({
                      ...styles,
                      border: state.isFocused ? "2px solid #AD5ADC" : "2px solid #00000061",
                      boxShadow: state.isFocused ? "0 0 0 1px #AD5ADC" : "none",
                      borderRadius: "20px",
                      minHeight: 40,
                      paddingLeft: 6,
                      ":hover": { borderColor: state.isFocused ? "#AD5ADC" : "#cfcfcf" },
                    }),
                    placeholder: (styles) => ({
                      ...styles,
                      color: "#6b6b6b",
                    }),
                    singleValue: (styles) => ({
                      ...styles,
                      color: "#000",
                      fontWeight: 400,
                    }),
                    dropdownIndicator: (styles, state) => ({
                      ...styles,
                      color: state.isFocused ? '#AD5ADC' : '#6b6b6b',
                      ':hover': { color: '#AD5ADC' },
                    }),
                    menu: (styles) => ({
                      ...styles,
                      borderRadius: "20px",
                      overflow: "hidden",
                      cursor: "pointer",
                    }),
                    option: (styles, { isFocused, isSelected }) => ({
                      ...styles,
                      backgroundColor: isSelected
                        ? '#E8DBF3'
                        : isFocused
                          ? '#F5ECFA'
                          : '#fff',
                      color: '#000',
                    }),
                  }}
                />
              </div>

              {preset === "custom" && (
                <div className="subscription-filter-group">
                  <div className="row g-2">
                    <div className="col-6">
                      <label className="subscription-filter-label">Start Date</label>
                      <input
                        type="date"
                        className="subscription-filter-input"
                        value={startDate ? moment(startDate).format("YYYY-MM-DD") : ""}
                        onChange={(e) => setStartDate(e.target.value ? moment(e.target.value).toDate() : null)}
                      />
                    </div>
                    <div className="col-6">
                      <label className="subscription-filter-label">End Date</label>
                      <input
                        type="date"
                        className="subscription-filter-input"
                        value={endDate ? moment(endDate).format("YYYY-MM-DD") : ""}
                        min={startDate ? moment(startDate).format("YYYY-MM-DD") : undefined}
                        onChange={(e) => setEndDate(e.target.value ? moment(e.target.value).toDate() : null)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        <div className="subscription-filter-footer">
          <button
            type="button"
            className="subscription-filter-btn subscription-filter-btn-reset"
            onClick={handleReset}
          >
            Reset
          </button>
          <button
            type="button"
            className="subscription-filter-btn subscription-filter-btn-apply"
            onClick={handleApply}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;

